import Phaser from 'phaser';
import { SaveStore } from '../state/SaveStore';
import { GameState } from '../state/GameState';
import { applyTheme } from '../render/themes';
import { makeFruit } from '../render/fruits';
import { ANIMAL_STATS } from '../config/animals';
import type { FruitKind } from '../types';

export class TitleScene extends Phaser.Scene {
  constructor() {
    super('TitleScene');
  }

  create(): void {
    const W = this.scale.width;
    const H = this.scale.height;
    const themeParts = applyTheme(this, 'meadow', H - 120);

    // Fullscreen toggle (top-right). Browsers only let us enter fullscreen
    // from a user gesture, so this is the cleanest entry point.
    const fsBtn = this.add
      .rectangle(W - 60, 30, 100, 36, 0x3d2914, 0.85)
      .setStrokeStyle(2, 0xffffff)
      .setDepth(80)
      .setInteractive({ useHandCursor: true });
    const fsTxt = this.add
      .text(W - 60, 30, '⛶ Fullscreen', {
        fontFamily: 'sans-serif',
        fontSize: '13px',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setDepth(81);
    const refreshFsLabel = () => {
      fsTxt.setText(this.scale.isFullscreen ? '⛶ Exit FS' : '⛶ Fullscreen');
    };
    refreshFsLabel();
    // Use pointerup (per Phaser docs) so the user-activation token
    // survives through to requestFullscreen.
    fsBtn.on('pointerup', () => {
      if (this.scale.isFullscreen) this.scale.stopFullscreen();
      else this.scale.startFullscreen();
      this.time.delayedCall(50, refreshFsLabel);
    });
    this.scale.on('enterfullscreen', refreshFsLabel);
    this.scale.on('leavefullscreen', refreshFsLabel);

    // Secret debug menu — Ctrl+Shift+D on keyboard, or tap the bottom-right
    // corner 5 times within 3 seconds on touch.
    this.input.keyboard?.on('keydown-D', (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey) {
        this.scene.start('DebugMenuScene');
      }
    });

    const hotspot = this.add
      .rectangle(W - 40, H - 40, 80, 80, 0x000000, 0)
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: false })
      .setDepth(100);
    let tapCount = 0;
    let firstTapAt = 0;
    hotspot.on('pointerdown', () => {
      const now = this.time.now;
      if (now - firstTapAt > 3000) {
        firstTapAt = now;
        tapCount = 1;
      } else {
        tapCount += 1;
      }
      if (tapCount >= 5) {
        this.scene.start('DebugMenuScene');
      }
    });

    // Keep clouds clear of the sun (top-left, ~ x<280, y<220) and the title
    // block (y between ~80 and ~230). Push offending clouds well below the
    // text and re-space them horizontally.
    const cloudYAboveTitle = 60;
    themeParts.cloudGroup.getChildren().forEach((c, i) => {
      const cloud = c as Phaser.GameObjects.Container;
      const overlapsSun = cloud.x < 280 && cloud.y < 220;
      const overlapsTitle = cloud.y > 50 && cloud.y < 200;
      if (overlapsSun || overlapsTitle) {
        cloud.y = cloudYAboveTitle + (i % 2) * 20;
      }
    });

    // Sun in the top-left
    const sunX = 130;
    const sunY = 130;
    const halo = this.add.circle(sunX, sunY, 92, 0xffe7a8, 0.35);
    this.add.circle(sunX, sunY, 60, 0xffd24a);
    // Slow pulse on the halo
    this.tweens.add({
      targets: [halo],
      scale: 1.1,
      alpha: 0.5,
      duration: 1800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.add
      .text(W / 2, 90, 'ANIMAL QUEST', {
        fontFamily: 'sans-serif',
        fontSize: '88px',
        color: '#3d2914',
        stroke: '#fff',
        strokeThickness: 6,
      })
      .setOrigin(0.5);
    this.add
      .text(W / 2, 160, 'an endless ride across 25 levels', {
        fontFamily: 'sans-serif',
        fontSize: '22px',
        color: '#3d2914',
      })
      .setOrigin(0.5);

    // Fruits in a line on the ground (the brown bit). Ground starts at H-120.
    const fruits: FruitKind[] = ['apple', 'berry', 'melon', 'pear', 'golden_apple', 'banana'];
    const fruitY = H - 80;
    fruits.forEach((kind, i) => {
      const fx = W / 2 + (i - (fruits.length - 1) / 2) * 110;
      const f = makeFruit(this, kind, fx, fruitY);
      f.setScale(1.6);
      this.tweens.add({
        targets: f,
        y: fruitY - 8,
        duration: 700 + i * 60,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    });

    // 2-Player race button — sits just under the subtitle
    const twoPBtn = this.add.container(W / 2, 200);
    const twoPBg = this.add.rectangle(0, 0, 360, 44, 0x6b3aa8).setStrokeStyle(2, 0xffffff);
    const twoPTxt = this.add
      .text(0, 0, '2-Player Battle', {
        fontFamily: 'sans-serif',
        fontSize: '20px',
        color: '#ffffff',
      })
      .setOrigin(0.5);
    twoPBtn.add([twoPBg, twoPTxt]);
    twoPBtn.setSize(360, 44).setInteractive({ useHandCursor: true });
    twoPBtn.on('pointerover', () => twoPBg.setFillStyle(0x8a4ec2));
    twoPBtn.on('pointerout', () => twoPBg.setFillStyle(0x6b3aa8));
    twoPBtn.on('pointerdown', () => this.scene.start('TwoPlayerSetupScene'));

    // 1-Player slot list label (sits just below the purple 2P button)
    this.add
      .text(W / 2, 240, '— 1 Player —', {
        fontFamily: 'sans-serif',
        fontSize: '14px',
        color: '#3d2914',
      })
      .setOrigin(0.5);

    // Slot list — 5 save slots stacked under the title.
    const slots = SaveStore.listSlots();
    const slotStartY = 275;
    const slotHeight = 56;
    const slotGap = 8;
    slots.forEach((slot, idx) => {
      const y = slotStartY + idx * (slotHeight + slotGap);
      this.makeSlotRow(W / 2, y, idx, slot);
    });
  }

  private makeSlotRow(
    cx: number,
    cy: number,
    idx: number,
    slot: ReturnType<typeof SaveStore.listSlots>[number],
  ): void {
    const c = this.add.container(cx, cy);
    const bg = this.add.rectangle(0, 0, 540, 52, 0x3d2914, 0.92).setStrokeStyle(2, 0xffffff);
    c.add(bg);

    const slotLabel = this.add
      .text(-250, 0, slot.name, {
        fontFamily: 'sans-serif',
        fontSize: '18px',
        color: '#ffd9a0',
      })
      .setOrigin(0, 0.5);
    c.add(slotLabel);

    if (slot.data) {
      const animal = ANIMAL_STATS[slot.data.animal]?.displayName ?? slot.data.animal;
      const detail = this.add
        .text(-130, 0, `Level ${slot.data.level}  •  ${animal}`, {
          fontFamily: 'sans-serif',
          fontSize: '18px',
          color: '#ffffff',
        })
        .setOrigin(0, 0.5);
      c.add(detail);

      const continueBtn = this.makeMiniButton(120, 0, 'Continue', 0x4a8a3a, () => {
        SaveStore.setActiveSlot(idx);
        const data = slot.data!;
        GameState.setCharacter(this.registry, data.character);
        GameState.setAnimal(this.registry, data.animal);
        GameState.setLevel(this.registry, data.level);
        this.scene.start('LevelIntroScene', { level: data.level });
      });
      c.add(continueBtn);

      const deleteBtn = this.makeMiniButton(230, 0, 'Delete', 0x6b1a1a, () => {
        SaveStore.clearSlot(idx);
        this.scene.restart();
      });
      c.add(deleteBtn);
    } else {
      const detail = this.add
        .text(-130, 0, '— empty —', {
          fontFamily: 'sans-serif',
          fontSize: '18px',
          color: '#888',
        })
        .setOrigin(0, 0.5);
      c.add(detail);

      const newBtn = this.makeMiniButton(180, 0, 'New Game', 0x6b3410, () => {
        SaveStore.setActiveSlot(idx);
        this.registry.set('character', undefined);
        this.registry.set('animal', undefined);
        this.registry.set('level', 1);
        this.scene.start('CharacterCreatorScene');
      });
      c.add(newBtn);
    }
  }

  private makeMiniButton(
    x: number,
    y: number,
    label: string,
    color: number,
    onClick: () => void,
  ): Phaser.GameObjects.Container {
    const btn = this.add.container(x, y);
    const bg = this.add.rectangle(0, 0, 96, 36, color).setStrokeStyle(2, 0xffffff);
    const txt = this.add
      .text(0, 0, label, { fontFamily: 'sans-serif', fontSize: '14px', color: '#ffffff' })
      .setOrigin(0.5);
    btn.add([bg, txt]);
    btn.setSize(96, 36).setInteractive({ useHandCursor: true });
    btn.on('pointerover', () => bg.setStrokeStyle(2, 0xffd24a));
    btn.on('pointerout', () => bg.setStrokeStyle(2, 0xffffff));
    btn.on('pointerdown', onClick);
    return btn;
  }

}
